import { AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import { authActions } from 'src/state/ducks/common/auth';
import { isLoginAuthRequest } from 'src/utils/axios/login-error-message';
import { loadingActions } from 'src/state/ducks/common/loading';
import { offlineActions } from 'src/state/ducks/common/offline';
import { getTokenValue } from 'src/state/ducks/common/auth/storage';
import { showToastMessage } from 'src/components/admin/message/toast-message';
import { uuidv4 } from '../generator';
import { debounce } from '../debounce-throttle';

// Import offline queue service
import { offlineQueueService } from '../../services/offline-queue.service';

export const requestAuthHandler = (store) => (request: AxiosRequestConfig) => {
  // Modify request here
  const token = getTokenValue();
  if (token) {
    request.headers = request.headers || {};
    if (!request.headers.Authorization) {
      request.headers.Authorization = `Bearer ${token}`;
    }
  }

  return request;
};

export const requestConvertFormDataHandler = (store) => (request: AxiosRequestConfig) => {
  if (request.data && !(request.data instanceof FormData)) {
    request.data = qs.stringify(request.data);
  }

  return request;
};

export const requestPaginationHandler = (store) => (request: AxiosRequestConfig) => {
  // modify pagination params ?page=1&per_page=10

  if (request.params && request.params.pagination) {
    Object.keys(request.params.pagination).forEach((key) => {
      request.params[key] = request.params.pagination[key];
    });

    request.params.pagination = undefined;
  }
  // if (request.params && request.params.filter) {
  //   Object.keys(request.params.filter).forEach((key) => {
  //     request.params.filter = undefined;
  //   });
  // }

  return request;
};

export const requestLoadingHandler = (store) => (request: AxiosRequestConfig) => {
  if (!request.skipLoadingHandler) {
    const apiRequestId = uuidv4();
    request.apiRequestId = apiRequestId;

    store.dispatch(loadingActions.addLoadingApi(apiRequestId));
  }

  return request;
};

export const responseLoadingHandler = (store) => (response: AxiosResponse) => {
  if (!response.config.skipLoadingHandler) {
    const { apiRequestId } = response.config;

    store.dispatch(loadingActions.removeLoadingApi(apiRequestId));
  }

  return response;
};

export const errorLoadingHandler = (store) => (error: any) => {
  if (!error.response) {
    store.dispatch(loadingActions.clearData());
  } else if (!error.response.config.skipLoadingHandler) {
    const { apiRequestId } = error.response.config;

    store.dispatch(loadingActions.removeLoadingApi(apiRequestId));
  }

  return error;
};

/**
 * Enhanced error handler with offline storage support
 */
export const errorHandler = (store) => async (error: any) => {
  // Handle errors
  if (typeof error !== 'undefined') {
    // Setup Error Message
  }

  if (error.response) {
    // Setup Generic Response Messages
    if (error.response.status === 401) {
      // Wrong password on /login returns 401 — do not wipe state so login failure message can show.
      if (!isLoginAuthRequest(error.config)) {
        store.dispatch(authActions.clearData());
      }
    } else if (error.response.status === 403) {
      // Forbidden
    } else if (error.response.status === 404) {
      // 'API Route is Missing or Undefined';
    } else if (error.response.status === 405) {
      // 'API Route Method Not Allowed';
    } else if (error.response.status === 422) {
      // Validation Message
    } else if (error.response.status >= 500) {
      // 'Server Error';
    }

    // Try to Use the Response Message
  } else {
    // Network error - implement offline storage
    await handleOfflineRequest(error.config, store);
  }

  return Promise.reject(error);
};

/**
 * Handle offline requests by storing them in local database
 */
const handleOfflineRequest = async (config: AxiosRequestConfig, store: any) => {
  try {
    // Check if this is a request that should be queued offline
    if (shouldQueueOffline(config)) {
      // Queue the request for later processing
      const requestId = await queueRequestOffline(config);

      // Show user-friendly message about offline storage
      debounce(
        1000,
        showToastMessage,
        `Request saved offline (ID: ${requestId}). Will sync when connection is restored.`,
        'info'
      );

      // Update UI to show offline status
      updateOfflineStatus(store, true);

      // Return a mock response to prevent the app from crashing
      return Promise.resolve({
        data: {
          success: false,
          offline: true,
          message: 'Request queued for offline processing',
          requestId,
          timestamp: new Date().toISOString(),
        },
        status: 202, // Accepted for processing
        statusText: 'Offline Queued',
        headers: {},
        config,
        request: {},
      });
    } else {
      // Show generic offline message for non-queueable requests
      debounce(
        1000,
        showToastMessage,
        'Unable to connect to server. Please check your Internet connection and try again',
        'error'
      );
    }
  } catch (queueError) {
    console.error('Failed to queue offline request:', queueError);

    // Fallback to original error message
    debounce(
      1000,
      showToastMessage,
      'Unable to connect to server. Please check your Internet connection and try again',
      'error'
    );
  }
};

/**
 * Determine if a request should be queued offline
 */
const shouldQueueOffline = (config: AxiosRequestConfig): boolean => {
  // Queue POST, PUT, DELETE requests (data modification)
  if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
    return true;
  }

  // Queue specific GET requests that are important
  if (config.method?.toLowerCase() === 'get') {
    const importantEndpoints = ['/api/orders', '/api/payments', '/api/customers', '/api/inventory', '/api/reports'];

    return importantEndpoints.some((endpoint) => config.url?.includes(endpoint));
  }

  return false;
};

/**
 * Queue a request for offline processing
 */
const queueRequestOffline = async (config: AxiosRequestConfig): Promise<number> => {
  try {
    // Create a Request object for the offline queue
    const request = new Request(config.url || '', {
      method: config.method || 'GET',
      headers: config.headers || {},
      body: config.data ? JSON.stringify(config.data) : null,
    });

    // Add metadata for better offline processing
    const options = {
      priority: getRequestPriority(config),
      tags: getRequestTags(config),
      maxRetries: 3,
    };

    // Queue the request
    const requestId = await offlineQueueService.queueRequest(request, options);

    console.log(`Request queued offline with ID: ${requestId}`, {
      url: config.url,
      method: config.method,
      priority: options.priority,
    });

    return requestId;
  } catch (error) {
    console.error('Failed to queue request offline:', error);
    throw error;
  }
};

/**
 * Get request priority based on endpoint and method
 */
const getRequestPriority = (config: AxiosRequestConfig): 'low' | 'normal' | 'high' => {
  const url = config.url || '';
  const method = config.method?.toLowerCase() || '';

  // High priority: payments, orders, critical data
  if (url.includes('/api/payments') || url.includes('/api/orders')) {
    return 'high';
  }

  // Normal priority: most data operations
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    return 'normal';
  }

  // Low priority: GET requests, non-critical data
  return 'low';
};

/**
 * Get request tags for categorization
 */
const getRequestTags = (config: AxiosRequestConfig): string[] => {
  const url = config.url || '';
  const tags: string[] = [];

  if (url.includes('/api/payments')) tags.push('payment');
  if (url.includes('/api/orders')) tags.push('order');
  if (url.includes('/api/customers')) tags.push('customer');
  if (url.includes('/api/inventory')) tags.push('inventory');
  if (url.includes('/api/reports')) tags.push('report');

  return tags;
};

/**
 * Update offline status in the store
 */
const updateOfflineStatus = (store: any, isOffline: boolean) => {
  // Update offline status in Redux store
  store.dispatch(offlineActions.setOfflineStatus(isOffline));
};

// Handle responses
export const successHandler = (store) => (response: AxiosResponse) => {
  // Update offline status when we get a successful response
  updateOfflineStatus(store, false);

  return response;
};
